/**
 * The tracking assets list beside the map (Diurnal parity 03-039; the Diurnal Tracking Assets panel, am:28964–29215;
 * docs/specs/tracker.md §7): the organisation's own aircraft and vehicles only (not public traffic), titled with
 * how many and how many are airborne, those needing attention first, each with its state in words, tracker, link,
 * altitude, speed, heading, flight time and the age of its position.
 */
import type { AircraftPosition } from './aircraft-layer.js';
import { flightStatusLabel } from './flight-status.js';
import {
  TRACKER_STATE_WORD,
  ageText,
  trackerSourceOf,
  type TrackerSourceId,
  type TrackerState,
} from './tracker.js';

/** Short tracker names for the list's badge (am:29136: V2T, ST, WS, TP). */
export const TRACKER_BADGE: Readonly<Partial<Record<TrackerSourceId, string>>> = {
  v2track: 'V2T',
  spidertracks: 'ST',
  websentinel: 'WS',
  tracplus: 'TP',
};

/**
 * The order of the list: SOS and lost tracking first (they need attention; the Diurnal tool put a lost aircraft
 * among the stale ones at the bottom), then airborne, on the ground, and no recent position; the newest position
 * first within each.
 */
const ORDER: Readonly<Record<TrackerState, number>> = { sos: 0, lost: 1, airborne: 2, parked: 3, stale: 4 };

export interface TrackingRow {
  readonly id: string;
  readonly label: string;
  readonly source: string;
  readonly badge: string;
  readonly state: TrackerState | null;
  readonly stateWord: string;
  /** "Satellite", "Cellular", or '' when the tracker does not say. */
  readonly link: string;
  /** "1,385 ft · 125 kt · 161°", the values the tracker sent. */
  readonly motion: string;
  /** "T/O 14:22 · FT 3.8h · LDG 18:08", or ''. */
  readonly flight: string;
  readonly age: string;
  readonly rfds: boolean;
  readonly alarm: boolean;
  /** Airborne by its tracker state or its own flag. */
  readonly airborne: boolean;
  /**
   * In the panel's short list (MB, 4 Nov 2026): an alert (SOS, lost tracking) or an aircraft airborne. The panel
   * shows only these while the pointer is outside it, and every row while it is pointed at or has the focus.
   */
  readonly compact: boolean;
}

export interface TrackingList {
  /** "Tracking assets (7, 3 airborne)". */
  readonly title: string;
  readonly rows: readonly TrackingRow[];
}

function motion(a: AircraftPosition): string {
  const parts: string[] = [];
  if (a.alt_ft !== null && Number.isFinite(a.alt_ft))
    parts.push(`${Math.round(a.alt_ft).toLocaleString('en-AU')} ft`);
  if (a.speed_kt !== null && Number.isFinite(a.speed_kt)) parts.push(`${Math.round(a.speed_kt)} kt`);
  if (a.heading_deg !== null && Number.isFinite(a.heading_deg))
    parts.push(`${Math.round(a.heading_deg) % 360}°`);
  return parts.join(' · ');
}

/** Airborne by its tracker state, else its own flag (no recent position or on the ground: not airborne). */
const isAirborne = (a: AircraftPosition): boolean =>
  a.state === 'airborne' || (a.airborne && a.state !== 'parked' && a.state !== 'stale');

/**
 * The panel's short list (MB, 4 Nov 2026): the rows needing attention and those airborne, in the list's order, and
 * how many rows the full list has beyond them.
 */
export function compactTrackingRows(list: TrackingList): {
  readonly rows: readonly TrackingRow[];
  readonly hidden: number;
} {
  const rows = list.rows.filter((r) => r.compact);
  return { rows, hidden: list.rows.length - rows.length };
}

export function trackingList(
  aircraft: readonly AircraftPosition[],
  nowMs: number,
  offsetH: number,
): TrackingList {
  const fleet = aircraft.filter((a) => a.fleet !== false);
  const time = (a: AircraftPosition): number => Date.parse(a.time) || 0;
  const sorted = [...fleet].sort(
    (x, y) =>
      (x.state ? ORDER[x.state] : 5) - (y.state ? ORDER[y.state] : 5) ||
      time(y) - time(x) ||
      (x.label < y.label ? -1 : x.label > y.label ? 1 : 0),
  );
  const airborne = fleet.filter((a) => a.airborne).length;
  return {
    title: `Tracking assets (${fleet.length}, ${airborne} airborne)`,
    rows: sorted.map((a) => {
      const source = trackerSourceOf(a.source);
      return {
        id: a.id,
        label: a.label,
        source: a.source,
        badge: (source && TRACKER_BADGE[source]) || a.source,
        state: a.state ?? null,
        stateWord: a.state ? TRACKER_STATE_WORD[a.state] : a.airborne ? 'Airborne' : 'On the ground',
        link: a.satellite === true ? 'Satellite' : a.satellite === false ? 'Cellular' : '',
        motion: motion(a),
        flight: a.flight ? flightStatusLabel(a.flight, offsetH) : '',
        age: ageText(nowMs, time(a)),
        rfds: a.rfds === true,
        alarm: a.state === 'sos' || a.state === 'lost',
        airborne: isAirborne(a),
        compact: a.state === 'sos' || a.state === 'lost' || isAirborne(a),
      };
    }),
  };
}
